@use "@styles/_variables/colors" as *;
@use "@styles/_variables/breakpoints" as *;
@use "@styles/_variables/sizes" as *;
@use "@styles/_variables/mixins" as *;
@use "@styles/_variables/typography" as *;

// Static chamfer geometry for the cards. Mirrors the technoCard vocabulary but
// uses its own local custom properties (never the registered --tc-* names) since
// these do not need to tween.
$cc-cut: 1.2rem;
$cc-line: 0.15rem;

.carousel {
  margin: 2.2rem 0;
  width: 100%;
}

.viewport {
  display: flex;
  align-items: stretch;
  gap: 1.2rem;
}

// Horizontal scroller: snaps per card, hides its scrollbar, and lets native
// touch swipe fall out of overflow scrolling for free.
.track {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  gap: 1.6rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.card {
  --cc-cut: #{$cc-cut};
  --cc-line: #{$cc-line};
  // Inner-loop chamfer offset for the ring: cut pulled in by the stroke.
  // 0.414 = sqrt(2) - 1 keeps the inner chamfer parallel at stroke distance.
  --cc-i: max(0px, calc(var(--cc-cut) - 0.414 * var(--cc-line)));

  position: relative;
  z-index: 0;
  // Three per view when the track is wide enough, but never narrower than
  // the floor (sized to the widest title line): below it the next card
  // peeks past the edge instead, so titles keep their two lines.
  flex: 0 0 max(calc((100% - 2 * 1.6rem) / 3), 19.5rem);
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  flex-direction: column;
  min-height: 16rem;
  padding: 2.2rem 2rem;
  color: $silver;
  backdrop-filter: blur(4px);

  // Chamfered octagon glass panel.
  clip-path: polygon(
    var(--cc-cut) 0,
    calc(100% - var(--cc-cut)) 0,
    100% var(--cc-cut),
    100% calc(100% - var(--cc-cut)),
    calc(100% - var(--cc-cut)) 100%,
    var(--cc-cut) 100%,
    0 calc(100% - var(--cc-cut)),
    0 var(--cc-cut)
  );

  // Translucent dark glass over faint machined hairline scanlines. The gold
  // slash ticks live on the role line (.role::after) so every diagonal in
  // the card shares the same skew.
  background:
    repeating-linear-gradient(
      0deg,
      transparent 0,
      transparent 0.35rem,
      rgba($main-light, 0.05) 0.35rem,
      rgba($main-light, 0.05) 0.4rem
    ),
    #00000044;

  transition:
    color 0.25s ease,
    text-shadow 0.25s ease,
    filter 0.25s ease;

  // Steel outline ring: outer loop clockwise + inner loop counter-clockwise, so
  // nonzero winding leaves only the thin band filled. Same technique as the
  // technoCard ::before, with static equal corners.
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(to left, $main-lightish, $main-light);
    clip-path: polygon(
      var(--cc-cut) 0,
      calc(100% - var(--cc-cut)) 0,
      100% var(--cc-cut),
      100% calc(100% - var(--cc-cut)),
      calc(100% - var(--cc-cut)) 100%,
      var(--cc-cut) 100%,
      0 calc(100% - var(--cc-cut)),
      0 var(--cc-cut),
      var(--cc-cut) 0,
      calc(var(--cc-line) + var(--cc-i)) var(--cc-line),
      var(--cc-line) calc(var(--cc-line) + var(--cc-i)),
      var(--cc-line) calc(100% - var(--cc-line) - var(--cc-i)),
      calc(var(--cc-line) + var(--cc-i)) calc(100% - var(--cc-line)),
      calc(100% - var(--cc-line) - var(--cc-i)) calc(100% - var(--cc-line)),
      calc(100% - var(--cc-line)) calc(100% - var(--cc-line) - var(--cc-i)),
      calc(100% - var(--cc-line)) calc(var(--cc-line) + var(--cc-i)),
      calc(100% - var(--cc-line) - var(--cc-i)) var(--cc-line),
      calc(var(--cc-line) + var(--cc-i)) var(--cc-line)
    );
  }

  &:hover {
    .reticle {
      opacity: 1;
      transform: scale(1);
    }

    .reticle::before {
      animation: card-scan 1.6s linear infinite;
    }

    .name,
    .year {
      color: $gold-light;
      text-shadow: $text-glow-faint;
    }
  }
}

// Target-lock reticle: four gold corner brackets, hidden at rest, plus a gold
// scanline that sweeps down the card while locked on.
.reticle {
  --scan-reach: 13rem;
  position: absolute;
  inset: 0.9rem;
  pointer-events: none;
  opacity: 0;
  transform: scale(0.85);
  filter: drop-shadow(0 0 0.4rem $gold-semi-transparent);
  background:
    linear-gradient($gold, $gold) 0 0 / 1.4rem 0.15rem,
    linear-gradient($gold, $gold) 0 0 / 0.15rem 1.4rem,
    linear-gradient($gold, $gold) 100% 0 / 1.4rem 0.15rem,
    linear-gradient($gold, $gold) 100% 0 / 0.15rem 1.4rem,
    linear-gradient($gold, $gold) 0 100% / 1.4rem 0.15rem,
    linear-gradient($gold, $gold) 0 100% / 0.15rem 1.4rem,
    linear-gradient($gold, $gold) 100% 100% / 1.4rem 0.15rem,
    linear-gradient($gold, $gold) 100% 100% / 0.15rem 1.4rem;
  background-repeat: no-repeat;
  transition:
    transform 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.4),
    opacity 0.15s ease;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 0.15rem;
    opacity: 0;
    background: linear-gradient(
      to right,
      transparent,
      $gold-semi-transparent 30%,
      $gold 50%,
      $gold-semi-transparent 70%,
      transparent
    );
  }
}

// Top data line: index left, role right, separated from the body by a
// hairline like a HUD data plate.
.head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  padding-bottom: 0.9rem;
  border-bottom: 0.1rem solid $main-medium-transp;
}

.index {
  font-family: $font-display;
  font-size: 1.2rem;
  letter-spacing: 0.3em;
  color: $main-light;
}

// Role readout: tracked small caps followed by a gold slash-tick cluster
// at the frame family's 45-degree lean, the same marks the section cards
// and the page frame wear.
.role {
  display: inline-flex;
  align-items: center;
  font-family: $font-display;
  font-size: 1.2rem;
  letter-spacing: 0.3em;

  &::after {
    content: "";
    width: 2.2rem;
    height: 0.7rem;
    margin-left: 0.6rem;
    background: repeating-linear-gradient(
      -45deg,
      $gold-semi-transparent 0,
      $gold-semi-transparent 0.2rem,
      transparent 0.2rem,
      transparent 0.8rem
    );
  }
}

.role-hacker {
  color: $main-lighter;
}

.role-mentor {
  color: $url;
}

.role-judge {
  color: $saffron;
}

// Clamped to two lines so a long title can never push the rhythm apart. The
// slot reserves both lines on every card, so the prize list below starts at
// the same y on every card in a row.
.name {
  margin-top: 1.2rem;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  min-height: 5.28rem; // two lines at 2.2rem / 1.2
  font-family: $font-display-bold;
  font-size: 2.2rem;
  line-height: 1.2;
  color: $main-lightestest;
  transition:
    color 0.25s ease,
    text-shadow 0.25s ease;
}

// Prize results, right-aligned beneath the title. The slot reserves two
// lines on every card (also when empty), so the foot rhythm holds no matter
// how many prizes a card carries.
.prizes {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 0.15rem;
  margin-top: 0.8rem;
  min-height: 3.2rem;
}

.prize {
  font-family: $font-display;
  font-size: 1.3rem;
  letter-spacing: 0.14em;
  margin-right: -0.14em;
  line-height: 1.15;
  text-align: right;
  color: $gold-light;
  text-shadow: 0 0 0.5rem rgba($gold, 0.4);
}

.year {
  letter-spacing: 0.04em;
  font-family: $font-display-bold;
  font-size: 3rem;
  line-height: 1;
  color: $main-lightest;
  transition:
    color 0.25s ease,
    text-shadow 0.25s ease;
}

// Bottom row: year anchored left, visit chip right, welded to the card's
// bottom edge so every card shares the same rhythm regardless of how many
// prize lines it carries.
.foot {
  margin-top: auto;
  padding-top: 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
}

// The card's single tap target: a slash chip echoing the frame's fins, with
// the label counter-skewed so it reads straight.
.visit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.3rem;
  font-family: $font-display;
  font-size: 1.3rem;
  letter-spacing: 0.3em;
  color: $main-lightest;
  text-decoration: none;
  border: 0.15rem solid $main-lighter;
  background: transparent;
  transform: skewX(-20deg);
  cursor: pointer;
  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
  @include hideTapHighlightColor();

  > span {
    display: inline-block;
    transform: skewX(20deg);
    margin-right: -0.3em;
  }

  &:hover,
  &:focus-visible {
    color: $gold-light;
    border-color: $gold;
    background: rgba($gold, 0.12);
    box-shadow: 0 0 0.8rem $gold-semi-transparent;
    text-shadow: $text-glow-faint;
    outline: none;
  }
}

// Chamfered steel square nav buttons with a chevron. The outline is a
// nonzero-winding ::before ring (same technique as the card's), so the steel
// band follows the chamfer diagonals instead of a rectangular border that
// vanishes at the cut corners.
.nav {
  --nav-cut: 0.8rem;
  --nav-line: 0.15rem;
  // Inner-loop chamfer offset for the ring: cut pulled in by the stroke.
  // 0.414 = sqrt(2) - 1 keeps the inner chamfer parallel at stroke distance.
  --nav-i: max(0px, calc(var(--nav-cut) - 0.414 * var(--nav-line)));

  position: relative;
  z-index: 0;
  flex: 0 0 auto;
  align-self: center;
  width: 4rem;
  height: 4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  cursor: pointer;
  color: $main-lighter;
  background: #00000044;
  clip-path: polygon(
    var(--nav-cut) 0,
    calc(100% - var(--nav-cut)) 0,
    100% var(--nav-cut),
    100% calc(100% - var(--nav-cut)),
    calc(100% - var(--nav-cut)) 100%,
    var(--nav-cut) 100%,
    0 calc(100% - var(--nav-cut)),
    0 var(--nav-cut)
  );
  transition:
    color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
  @include hideTapHighlightColor();

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: $main-darkish;
    transition: background-color 0.2s ease;
    clip-path: polygon(
      var(--nav-cut) 0,
      calc(100% - var(--nav-cut)) 0,
      100% var(--nav-cut),
      100% calc(100% - var(--nav-cut)),
      calc(100% - var(--nav-cut)) 100%,
      var(--nav-cut) 100%,
      0 calc(100% - var(--nav-cut)),
      0 var(--nav-cut),
      var(--nav-cut) 0,
      calc(var(--nav-line) + var(--nav-i)) var(--nav-line),
      var(--nav-line) calc(var(--nav-line) + var(--nav-i)),
      var(--nav-line) calc(100% - var(--nav-line) - var(--nav-i)),
      calc(var(--nav-line) + var(--nav-i)) calc(100% - var(--nav-line)),
      calc(100% - var(--nav-line) - var(--nav-i)) calc(100% - var(--nav-line)),
      calc(100% - var(--nav-line)) calc(100% - var(--nav-line) - var(--nav-i)),
      calc(100% - var(--nav-line)) calc(var(--nav-line) + var(--nav-i)),
      calc(100% - var(--nav-line) - var(--nav-i)) var(--nav-line),
      calc(var(--nav-line) + var(--nav-i)) var(--nav-line)
    );
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    color: $gold-light;
    transform: scale(1.08);
    box-shadow: 0 0 0.8rem $gold-semi-transparent;
    outline: none;

    &::before {
      background: $gold;
    }
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
    box-shadow: none;
  }
}

.chevron {
  width: 2rem;
  height: 2rem;
}

// Slash-pip indicators echoing the frame's page pips.
.pips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.9rem;
  margin-top: 1.6rem;
}

.pip {
  width: 1rem;
  height: 0.4rem;
  padding: 0;
  border: none;
  cursor: pointer;
  background: $main-darkish;
  transform: skewX(-20deg);
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease;
  @include hideTapHighlightColor();

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0.4rem $gold-semi-transparent;
  }
}

.pip-active {
  background: $gold;
  box-shadow: $text-glow-faint;
}

// Two cards per view below tablet width, with tighter type and geometry.
@media all and (max-width: $mobile-large) {
  .card {
    flex-basis: max(calc((100% - 1.6rem) / 2), 19.5rem);
    min-height: 14rem;
    padding: 1.8rem 1.6rem;
  }

  .reticle {
    --scan-reach: 11rem;
  }

  .name {
    font-size: 2rem;
  }

  .year {
    font-size: 2.6rem;
  }

  .foot {
    padding-top: 1.3rem;
  }

  .visit {
    padding: 0.5rem 1.1rem;
    font-size: 1.2rem;
  }

  .nav {
    width: 3.4rem;
    height: 3.4rem;
  }

  .chevron {
    width: 1.7rem;
    height: 1.7rem;
  }
}

// One card per view on phones.
@media all and (max-width: $mobile-small) {
  .viewport {
    gap: 0.8rem;
  }

  .card {
    flex-basis: 100%;
    min-height: 13rem;
    padding: 1.6rem 1.4rem;
  }

  .reticle {
    --scan-reach: 10rem;
  }

  .index,
  .role {
    font-size: 1.1rem;
  }

  .prizes {
    min-height: 3rem;
  }

  .prize {
    font-size: 1.2rem;
  }

  .name {
    font-size: 1.9rem;
  }

  .year {
    font-size: 2.4rem;
  }

  .foot {
    padding-top: 1.2rem;
  }

  .visit {
    padding: 0.45rem 1rem;
    font-size: 1.2rem;
  }

  .nav {
    width: 3rem;
    height: 3rem;
  }

  .chevron {
    width: 1.5rem;
    height: 1.5rem;
  }
}

@keyframes card-scan {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(var(--scan-reach));
    opacity: 0;
  }
}
